<template>
  <div class="analytics-hub">
    <!-- 页头 -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("analytics.title") }}</h1>
      </div>
    </div>

    <!-- 维度切换 -->
    <div class="ah-seg-row">
      <el-radio-group v-model="mode" size="small" class="ah-seg" @change="applyMode">
        <el-radio-button label="overview"><i class="el-icon-data-analysis"></i> {{ $t("analytics.overview") }}</el-radio-button>
        <el-radio-button label="date"><i class="el-icon-date"></i> {{ $t("analytics.byDate") }}</el-radio-button>
        <el-radio-button label="machine"><i class="el-icon-cpu"></i> {{ $t("analytics.byMachine") }}</el-radio-button>
        <el-radio-button label="product"><i class="el-icon-goods"></i> {{ $t("analytics.byProduct") }}</el-radio-button>
        <el-radio-button label="user"><i class="el-icon-user"></i> {{ $t("analytics.byUser") }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 共享控制条 -->
    <div class="ah-controls radius">
      <el-input
        v-if="isMer"
        v-model="userName"
        clearable
        size="small"
        class="ah-field"
        :placeholder="$t('TimeControl.userAccount')"
        @blur="onUserNameBlur"
      ></el-input>

      <!-- 度量：销售量 / 销售额 -->
      <el-radio-group v-if="mode !== 'user'" v-model="metric" size="small" class="ah-metric" @change="loadMode">
        <el-radio-button label="num">{{ $t("analytics.metricVolume") }}</el-radio-button>
        <el-radio-button label="sales">{{ $t("analytics.metricRevenue") }}</el-radio-button>
      </el-radio-group>

      <!-- 粒度 -->
      <el-select
        v-if="granularityOptions.length"
        v-model="granularity"
        size="small"
        class="ah-field ah-field--sm"
        @change="onGranularityChange"
      >
        <el-option v-for="opt in granularityOptions" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
      </el-select>

      <!-- 日期范围 -->
      <el-date-picker
        v-model="dateRange"
        :type="pickerType"
        :format="pickerFormat"
        :value-format="pickerFormat"
        :clearable="false"
        :picker-options="pickerOptions"
        size="small"
        class="ah-field ah-field--date"
        range-separator="→"
        :start-placeholder="$t('searchMachineProfit.start')"
        :end-placeholder="$t('searchMachineProfit.end')"
      ></el-date-picker>

      <!-- 机器多选（date / machine / product） -->
      <el-select
        v-if="mode === 'date' || mode === 'machine' || mode === 'product'"
        collapse-tags
        multiple
        :multiple-limit="10"
        v-model.trim="machineSel"
        v-el-select-loadmore="loadMoreMachine"
        filterable
        remote
        :remote-method="filterMachine"
        size="small"
        class="ah-field"
        :placeholder="$t('MachineProfit.qbjiqi')"
      >
        <el-option
          v-for="item in machineOptions"
          :key="item.equipmentNo"
          :label="item.equipmentName || item.name"
          :value="item.equipmentNo"
        ></el-option>
      </el-select>

      <!-- 商品多选（machine / product） -->
      <el-select
        v-if="mode === 'machine' || mode === 'product'"
        collapse-tags
        multiple
        :multiple-limit="10"
        v-model.trim="productSel"
        v-el-select-loadmore="loadMoreProduct"
        filterable
        remote
        :remote-method="filterProduct"
        size="small"
        class="ah-field"
        :placeholder="$t('MachineProfit.qbshpin')"
      >
        <el-option v-for="item in productOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
      </el-select>

      <!-- 用户模式快速搜索 -->
      <el-input
        v-if="mode === 'user'"
        v-model="userSearch.userName"
        clearable
        size="small"
        class="ah-field"
        prefix-icon="el-icon-search"
        :placeholder="$t('searchUserProfit.userName')"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      ></el-input>

      <!-- Top-N（machine / product 排行） -->
      <el-select
        v-if="mode === 'machine' || mode === 'product'"
        v-model="topN"
        size="small"
        class="ah-field ah-field--sm"
        @change="onTopNChange"
      >
        <el-option v-for="n in [3, 5, 10, 20]" :key="n" :label="$t('analytics.topN', { n })" :value="String(n)"></el-option>
      </el-select>

      <div class="ah-controls__actions">
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">{{ $t("common.search") }}</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">{{ $t("common.reset") }}</el-button>
        <el-button size="small" icon="el-icon-download" @click="handleExport">{{ $t("analytics.export") }}</el-button>
      </div>
    </div>

    <!-- ===================== 概览 ===================== -->
    <div v-if="mode === 'overview'" v-loading="ovLoading" class="ah-panel">
      <section class="ah-card ah-card--full">
        <div class="ah-card__title">{{ $t("analytics.trend") }}</div>
        <div id="ov-trend" class="ah-chart ah-chart--tall"></div>
      </section>

      <div class="ah-grid ah-grid--3">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.shareByPayment") }}</div>
          <div id="ov-pie-pay" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.shareByMachine") }}</div>
          <div id="ov-pie-machine" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.shareByProduct") }}</div>
          <div id="ov-pie-product" class="ah-chart"></div>
        </section>
      </div>

      <section class="ah-card ah-card--full">
        <div class="ah-card__title">{{ $t("analytics.refundsTitle") }} <span class="ah-card__hint">{{ $t("analytics.refundsNote") }}</span></div>
        <div v-loading="refundStatsLoading" class="ah-refunds">
          <div class="ah-refund-stat">
            <span class="ah-refund-stat__val vtm-num">{{ refundStats.count }}</span>
            <span class="ah-refund-stat__label">{{ $t("analytics.refundsCount") }}</span>
          </div>
          <div class="ah-refund-stat">
            <span class="ah-refund-stat__val vtm-num">{{ money(refundStats.amount) }}</span>
            <span class="ah-refund-stat__label">{{ $t("analytics.refundsAmount") }}</span>
          </div>
          <div class="ah-refund-stat">
            <span class="ah-refund-stat__val vtm-num" style="color: var(--vtm-ok)">{{ refundStats.success }}</span>
            <span class="ah-refund-stat__label">{{ $t("analytics.refundsSuccess") }}</span>
          </div>
          <div class="ah-refund-stat">
            <span class="ah-refund-stat__val vtm-num" style="color: var(--vtm-crit)">{{ refundStats.failed }}</span>
            <span class="ah-refund-stat__label">{{ $t("analytics.refundsFailed") }}</span>
          </div>
        </div>
      </section>

      <div class="ah-grid ah-grid--3">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.leaderboard") }} · {{ $t("analytics.byUser") }}</div>
          <vtm-table
            table-id="ah-lead-user"
            :columns="leaderUserColumns"
            :data="ovLeaderUser"
            :quick-search="false"
            :border="true"
            max-height="320"
          ></vtm-table>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.leaderboard") }} · {{ $t("analytics.byMachine") }}</div>
          <vtm-table
            table-id="ah-lead-machine"
            :columns="leaderMachineColumns"
            :data="ovLeaderMachine"
            :quick-search="false"
            :border="true"
            max-height="320"
          ></vtm-table>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.leaderboard") }} · {{ $t("analytics.byProduct") }}</div>
          <vtm-table
            table-id="ah-lead-product"
            :columns="leaderProductColumns"
            :data="ovLeaderProduct"
            :quick-search="false"
            :border="true"
            max-height="320"
          ></vtm-table>
        </section>
      </div>
    </div>

    <!-- ===================== 按日期 ===================== -->
    <div v-else-if="mode === 'date'" class="ah-panel">
      <section class="ah-card ah-card--full">
        <div class="ah-card__title">{{ $t("analytics.trend") }}</div>
        <div id="date-trend" class="ah-chart ah-chart--tall"></div>
      </section>
      <list-page
        :loading="dateLoading"
        :error="dateError"
        :empty="!dateTableList.length"
        :total="datePage.totalSize || 0"
        :page-index="datePage.pageIndex"
        :page-size="datePage.pageSize"
        @page-change="loadDateTable"
        @size-change="dateSizeChange"
        @retry="loadDateTable(datePage.pageIndex)"
      >
        <vtm-table
          table-id="ah-date"
          :columns="dateColumns"
          :data="dateTableList"
          :loading="dateLoading"
          :quick-search="false"
          :border="true"
          max-height="638"
          show-summary
          :summary-method="p => summarize(p, dateOrder)"
        ></vtm-table>
      </list-page>
    </div>

    <!-- ===================== 按机器 ===================== -->
    <div v-else-if="mode === 'machine'" class="ah-panel">
      <div class="ah-grid ah-grid--pie-trend">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.machineShare") }}</div>
          <div id="m-pie" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.machineTrend") }}</div>
          <div id="m-trend" class="ah-chart"></div>
        </section>
      </div>
      <div class="ah-grid ah-grid--2">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("MachineProfit.sbphangban") }}</div>
          <div id="m-rank" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title-row">
            <span class="ah-card__title">{{ $t("analytics.drilldownMachine") }}</span>
            <el-select v-model="machineSelectedNo" size="small" style="width: 200px" @change="renderMachineDrill">
              <el-option
                v-for="m in machineRankNames"
                :key="m.equipmentNo"
                :label="m.equipmentName"
                :value="m.equipmentNo"
              ></el-option>
            </el-select>
          </div>
          <div id="m-drill" class="ah-chart"></div>
        </section>
      </div>
      <list-page
        :loading="machineLoading"
        :error="machineError"
        :empty="!machineTableList.length"
        :total="machinePage.totalSize || 0"
        :page-index="machinePage.pageIndex"
        :page-size="machinePage.pageSize"
        @page-change="loadMachineTable"
        @size-change="machineSizeChange"
        @retry="loadMachineTable(machinePage.pageIndex)"
      >
        <vtm-table
          table-id="ah-machine"
          :columns="machineColumns"
          :data="machineTableList"
          :loading="machineLoading"
          :quick-search="false"
          :border="true"
          max-height="638"
          show-summary
          :summary-method="p => summarize(p, machineOrder)"
        ></vtm-table>
      </list-page>
    </div>

    <!-- ===================== 按商品 ===================== -->
    <div v-else-if="mode === 'product'" class="ah-panel">
      <div class="ah-grid ah-grid--pie-trend">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.productShare") }}</div>
          <div id="p-pie" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("analytics.productTrend") }}</div>
          <div id="p-trend" class="ah-chart"></div>
        </section>
      </div>
      <div class="ah-grid ah-grid--2">
        <section class="ah-card">
          <div class="ah-card__title">{{ $t("MachineProfit.spphangban") }}</div>
          <div id="p-rank" class="ah-chart"></div>
        </section>
        <section class="ah-card">
          <div class="ah-card__title-row">
            <span class="ah-card__title">{{ $t("analytics.drilldownProduct") }}</span>
            <el-select v-model="productSelectedId" size="small" style="width: 200px" @change="renderProductDrill">
              <el-option
                v-for="p in productRankNames"
                :key="p.goodsId"
                :label="p.goodsName"
                :value="p.goodsId"
              ></el-option>
            </el-select>
          </div>
          <div id="p-drill" class="ah-chart"></div>
        </section>
      </div>
      <list-page
        :loading="productLoading"
        :error="productError"
        :empty="!productTableList.length"
        :total="productPage.totalSize || 0"
        :page-index="productPage.pageIndex"
        :page-size="productPage.pageSize"
        @page-change="loadProductTable"
        @size-change="productSizeChange"
        @retry="loadProductTable(productPage.pageIndex)"
      >
        <vtm-table
          table-id="ah-product"
          :columns="productColumns"
          :data="productTableList"
          :loading="productLoading"
          :quick-search="false"
          :border="true"
          max-height="638"
          show-summary
          :summary-method="p => summarize(p, productOrder)"
        ></vtm-table>
      </list-page>
    </div>

    <!-- ===================== 按用户 ===================== -->
    <div v-else-if="mode === 'user'" class="ah-panel">
      <list-page
        :loading="userLoading"
        :error="userError"
        :empty="!userTableList.length"
        :total="userPage.totalSize || 0"
        :page-index="userPage.pageIndex"
        :page-size="userPage.pageSize"
        @page-change="loadUserTable"
        @size-change="userSizeChange"
        @retry="loadUserTable(userPage.pageIndex)"
      >
        <vtm-table
          table-id="ah-user"
          :columns="userColumns"
          :data="userTableList"
          :loading="userLoading"
          :quick-search="false"
          :border="true"
          max-height="638"
          show-summary
          :summary-method="p => summarize(p, userOrder)"
        ></vtm-table>
      </list-page>
    </div>
  </div>
</template>

<script>
  // 数据分析中心：整合 DateProfit / MachineProfit / ProductProfit / UserProfit 四个统计页，
  // 通过维度切换 (overview | date | machine | product | user) 呈现于同一页面。
  // 端点与请求参数与原页面保持一致；图表复用 chartOptions.js 的共享构建器。
  import * as echarts from "echarts";
  import { formatDate } from "../../common/formatDate";
  import { pieOption, trendLineOption, rankingBarOption } from "@/utils/chartOptions";
  import ListPage from "_c/common/ListPage.vue";
  import VtmTable from "_c/common/VtmTable.vue";

  // ECharts 实例集（模块级，避免响应式包裹）
  let charts = {};

  export default {
    name: "AnalyticsHub",
    components: {
      ListPage,
      VtmTable
    },
    directives: {
      "el-select-loadmore": {
        bind(el, binding) {
          const wrap = el.querySelector(".el-select-dropdown .el-select-dropdown__wrap");
          if (!wrap) {
            return;
          }
          wrap.addEventListener("scroll", function () {
            const condition = this.scrollHeight - this.scrollTop - 15 <= this.clientHeight;
            if (condition) {
              binding.value();
            }
          });
        }
      }
    },
    data() {
      return {
        mode: "overview",
        isMer: false,
        userName: "",
        // 共享控件
        metric: "num", // num | sales
        granularity: "day", // date: hour|day|month ; machine/product: day|month
        dateRange: [],
        machineSel: [],
        productSel: [],
        topN: "20",
        // 概览
        ovLoading: false,
        ovLeaderUser: [],
        ovLeaderMachine: [],
        ovLeaderProduct: [],
        // 退款速览（来自 ordersRefund 记录）
        refundStatsLoading: false,
        refundStats: { count: 0, amount: 0, success: 0, failed: 0 },
        // 按日期
        dateTableList: [],
        dateOrder: { num: 0, sales: 0 },
        datePage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        dateLoading: false,
        dateError: false,
        // 按机器
        machineTableList: [],
        machineOrder: { num: 0, sales: 0 },
        machinePage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        machineLoading: false,
        machineError: false,
        machineRankData: [],
        machineRankNames: [],
        machineSelectedNo: "",
        // 按商品
        productTableList: [],
        productOrder: { num: 0, sales: 0 },
        productPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        productLoading: false,
        productError: false,
        productRankData: [],
        productRankNames: [],
        productSelectedId: "",
        // 按用户
        userTableList: [],
        userOrder: { num: 0, sales: 0 },
        userPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        userLoading: false,
        userError: false,
        userSearch: { userName: "", startDate: "", endDate: "" },
        // 懒加载下拉
        machineOptions: [],
        productOptions: [],
        selMachine: { pageNum: 1, pageSize: 10, name: "" },
        selProduct: { pageNum: 1, pageSize: 10, name: "" },
        lockMachine: true,
        lockProduct: true,
        pickerOptions: {
          shortcuts: [
            {
              text: this.$t("txReview.today"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setHours(0, 0, 0, 0);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days7"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("txReview.days30"),
              onClick(picker) {
                const end = new Date();
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                picker.$emit("pick", [start, end]);
              }
            }
          ]
        }
      };
    },
    computed: {
      metricLabel() {
        return this.metric === "num" ? this.$t("MachineProfit.xsliang") : this.$t("MachineProfit.xshoue");
      },
      // 粒度对应的数值类型（原页面的 type 语义保持不变）
      // date: hour=1 day=2 month=3 ; machine/product: day=1 month=2
      dayTypeInt() {
        if (this.mode === "date") {
          return this.granularity === "hour" ? 1 : this.granularity === "month" ? 3 : 2;
        }
        return this.granularity === "month" ? 2 : 1;
      },
      granularityOptions() {
        if (this.mode === "date") {
          return [
            { value: "hour", label: this.$t("analytics.granHour") },
            { value: "day", label: this.$t("analytics.granDay") },
            { value: "month", label: this.$t("analytics.granMonth") }
          ];
        }
        if (this.mode === "machine" || this.mode === "product") {
          return [
            { value: "day", label: this.$t("analytics.granDay") },
            { value: "month", label: this.$t("analytics.granMonth") }
          ];
        }
        return [];
      },
      pickerType() {
        if (this.mode === "date") {
          return this.granularity === "hour" ? "datetimerange" : this.granularity === "month" ? "monthrange" : "daterange";
        }
        if ((this.mode === "machine" || this.mode === "product") && this.granularity === "month") {
          return "monthrange";
        }
        return "daterange";
      },
      pickerFormat() {
        if (this.mode === "date" && this.granularity === "hour") {
          return "yyyy-MM-dd HH:mm:ss";
        }
        if ((this.mode === "date" || this.mode === "machine" || this.mode === "product") && this.granularity === "month") {
          return "yyyy-MM";
        }
        return "yyyy-MM-dd";
      },
      dateColumns() {
        return [
          { prop: "orderDate", label: this.$t("dateOrder.strCreateDate"), align: "center" },
          { prop: "num", label: this.$t("dateOrder.allPurchaseNum"), width: 130, align: "center" },
          { prop: "sales", label: this.$t("dateOrder.allDuePrice"), width: 130, align: "center" }
        ];
      },
      machineColumns() {
        return [
          { prop: "equipmentName", label: this.$t("machineOrder.equipmentName"), align: "center" },
          { prop: "equipmentNo", label: this.$t("machineOrder.equipmentNo"), align: "center" },
          { prop: "productnum", label: this.$t("MachineProfit.productTotalNum"), align: "center" },
          { prop: "num", label: this.$t("MachineProfit.totalNum"), align: "center" },
          { prop: "sales", label: this.$t("MachineProfit.totalSales"), align: "center" }
        ];
      },
      productColumns() {
        return [
          { prop: "xuhao", label: this.$t("ProductProfit.xuhao"), width: 80, align: "center" },
          { prop: "goodsName", label: this.$t("ProductProfit.goodsName"), align: "center" },
          { prop: "equipmentTotalNum", label: this.$t("ProductProfit.machineTotalNum"), align: "center" },
          { prop: "num", label: this.$t("MachineProfit.totalNum"), align: "center" },
          { prop: "sales", label: this.$t("MachineProfit.totalSales"), align: "center" },
          { prop: "cost", label: this.$t("ProductProfit.totalCost"), align: "center" },
          { prop: "profit", label: this.$t("ProductProfit.totalProfit"), align: "center" }
        ];
      },
      userColumns() {
        return [
          { prop: "userName", label: this.$t("userOrder.userName"), align: "center" },
          { prop: "num", label: this.$t("userOrder.allPurchaseNum"), width: 130, align: "center" },
          { prop: "sales", label: this.$t("userOrder.allDuePrice"), width: 130, align: "center" }
        ];
      },
      leaderUserColumns() {
        return [
          { prop: "userName", label: this.$t("userOrder.userName"), align: "center" },
          { prop: "num", label: this.$t("MachineProfit.xsliang"), width: 100, align: "center" },
          { prop: "sales", label: this.$t("MachineProfit.xshoue"), width: 110, align: "center" }
        ];
      },
      leaderMachineColumns() {
        return [
          { prop: "equipmentName", label: this.$t("machineOrder.equipmentName"), align: "center" },
          { prop: "num", label: this.$t("MachineProfit.xsliang"), width: 100, align: "center" },
          { prop: "sales", label: this.$t("MachineProfit.xshoue"), width: 110, align: "center" }
        ];
      },
      leaderProductColumns() {
        return [
          { prop: "goodsName", label: this.$t("ProductProfit.goodsName"), align: "center" },
          { prop: "num", label: this.$t("MachineProfit.xsliang"), width: 100, align: "center" },
          { prop: "sales", label: this.$t("MachineProfit.xshoue"), width: 110, align: "center" }
        ];
      }
    },
    mounted() {
      if (localStorage.utype == 4) {
        this.isMer = true;
      }
      const dim = this.$route.query.dim;
      if (["overview", "date", "machine", "product", "user"].indexOf(dim) !== -1) {
        this.mode = dim;
      }
      window.addEventListener("resize", this.handleResize);
      this.applyDefaults();
      this.$nextTick(() => this.loadMode());
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.handleResize);
      this.disposeCharts();
    },
    methods: {
      // ---------------- 图表基础设施 ----------------
      renderChart(id, option) {
        this.$nextTick(() => {
          const el = document.getElementById(id);
          if (!el) {
            return;
          }
          let inst = charts[id];
          if (!inst) {
            inst = echarts.init(el, "macarons");
            charts[id] = inst;
          }
          inst.clear();
          inst.setOption(option);
          inst.resize();
        });
      },
      disposeCharts() {
        Object.keys(charts).forEach(key => {
          try {
            charts[key].dispose();
          } catch (e) {
            /* ignore */
          }
        });
        charts = {};
      },
      handleResize() {
        Object.keys(charts).forEach(key => {
          if (charts[key]) {
            charts[key].resize();
          }
        });
      },
      // ---------------- 模式与默认值 ----------------
      applyMode() {
        // 维度切换仅改内部状态，不写回 URL —— 避免 query 变化在标签栏重复生成页签
        this.disposeCharts();
        this.applyDefaults();
        this.$nextTick(() => this.loadMode());
      },
      applyDefaults() {
        this.metric = "num";
        this.topN = "20";
        this.machineSel = [];
        this.productSel = [];
        this.granularity = "day";
        this.userSearch = { userName: "", startDate: "", endDate: "" };
        this.dateRange = this.computeDefaultRange();
      },
      onGranularityChange() {
        this.dateRange = this.computeDefaultRange();
        this.loadMode();
      },
      onTopNChange() {
        if (this.mode === "machine") {
          this.loadMachineRank();
        } else if (this.mode === "product") {
          this.loadProductRank();
        }
      },
      onUserNameBlur() {
        this.selMachine.pageNum = 1;
        this.selProduct.pageNum = 1;
        this.machineOptions = [];
        this.productOptions = [];
        this.getSelectMachine();
        this.getSelectProduct();
      },
      computeDefaultRange() {
        const now = new Date();
        const pad = n => (n < 10 ? "0" : "") + n;
        const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const ym = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
        const ymdhms = d => `${ymd(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        if (this.mode === "date" && this.granularity === "hour") {
          const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          return [ymdhms(start), ymdhms(now)];
        }
        if ((this.mode === "date" || this.mode === "machine" || this.mode === "product") && this.granularity === "month") {
          const start = new Date(now);
          start.setMonth(start.getMonth() - 11);
          return [ym(start), ym(now)];
        }
        const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return [ymd(start), ymd(now)];
      },
      loadMode() {
        switch (this.mode) {
          case "overview":
            this.loadOverview();
            break;
          case "date":
            this.loadDateTrend();
            this.loadDateTable();
            break;
          case "machine":
            this.ensureMachineOptions();
            this.ensureProductOptions();
            this.loadMachineCharts();
            this.loadMachineRank();
            this.loadMachineTable();
            break;
          case "product":
            this.ensureMachineOptions();
            this.ensureProductOptions();
            this.loadProductCharts();
            this.loadProductRank();
            this.loadProductTable();
            break;
          case "user":
            this.loadUserTable();
            break;
        }
      },
      handleSearch() {
        if (!this.dateSpanOk()) {
          return;
        }
        if (this.mode === "user") {
          this.userSearch.startDate = this.dateRange && this.dateRange[0] ? this.dateRange[0] : "";
          this.userSearch.endDate = this.dateRange && this.dateRange[1] ? this.dateRange[1] : "";
        }
        this.loadMode();
      },
      handleReset() {
        this.applyDefaults();
        this.loadMode();
      },
      // 时间跨度校验（沿用原页面阈值）
      dateSpanOk() {
        const arr = this.dateRange;
        if (this.mode === "overview" || this.mode === "user") {
          return true;
        }
        if (!arr || arr.length < 2) {
          this.$message.warning(this.$t("DateProfit.kuaduDay"));
          return false;
        }
        const span = new Date(arr[1]).getTime() - new Date(arr[0]).getTime();
        if (this.mode === "date" && this.granularity === "hour") {
          if (span >= 259200000) {
            this.$message.warning(this.$t("DateProfit.kuaduHour"));
            return false;
          }
        } else if (this.granularity === "month") {
          if (span >= 157680000000) {
            this.$message.warning(this.$t("DateProfit.kuaduMonth"));
            return false;
          }
        } else {
          if (span >= 7776000000) {
            this.$message.warning(this.$t("DateProfit.kuaduDay"));
            return false;
          }
        }
        return true;
      },
      // ---------------- 概览 ----------------
      loadOverview() {
        const start = this.dateRange && this.dateRange[0] ? this.dateRange[0] : "";
        const end = this.dateRange && this.dateRange[1] ? this.dateRange[1] : "";
        this.ovLoading = true;
        this.loadRefundStats();
        let pending = 0;
        const done = () => {
          pending -= 1;
          if (pending <= 0) {
            this.ovLoading = false;
          }
        };
        // 收益趋势
        pending += 1;
        this.$api
          .request(this.$url.Consoles.getSalesDay, this.$method.get, {
            startDate: start,
            endDate: end,
            searchType: this.metric,
            type: 2
          })
          .then(res => {
            if (res.code === 0) {
              const result = res.data || [];
              const xData = result.map(r => r.orderDate);
              const series = [{ name: this.metricLabel, data: result.map(r => r[this.metric]) }];
              this.renderChart("ov-trend", trendLineOption({ xData, series, area: true }));
            }
            done();
          })
          .catch(done);
        // 占比图
        pending += 1;
        this.$api
          .request(this.$url.Consoles.getProportion, this.$method.get, { startDate: start, endDate: end })
          .then(res => {
            if (res.code === 0) {
              const data = res.data || {};
              const pay = (data.payTypeList || []).map(p => ({ name: p.payTypeName, value: Math.abs(parseFloat(p.profit) || 0) }));
              const machine = (data.equipmentList || []).map(m => ({ name: m.equipmentName, value: parseFloat(m.profit) || 0 }));
              const product = (data.goodsList || []).map(g => ({ name: g.goodsName, value: parseFloat(g.profit) || 0 }));
              this.renderChart("ov-pie-pay", pieOption({ data: pay, name: this.$t("analytics.shareByPayment") }));
              this.renderChart("ov-pie-machine", pieOption({ data: machine, name: this.$t("analytics.shareByMachine") }));
              this.renderChart("ov-pie-product", pieOption({ data: product, name: this.$t("analytics.shareByProduct") }));
            }
            done();
          })
          .catch(done);
        // 排行榜（用户 / 设备 / 商品）
        const rankParams = { startDate: start, endDate: end, pageIndex: 1, pageSize: 10 };
        pending += 1;
        this.$api
          .request(this.$url.Consoles.getSalesUser, this.$method.get, { ...rankParams })
          .then(res => {
            if (res.code === 0) {
              this.ovLeaderUser = Array.isArray(res.data) ? res.data : res.data.records || [];
            }
            done();
          })
          .catch(done);
        pending += 1;
        this.$api
          .request(this.$url.Consoles.getSalesMachine, this.$method.get, { ...rankParams })
          .then(res => {
            if (res.code === 0) {
              this.ovLeaderMachine = Array.isArray(res.data) ? res.data : res.data.records || [];
            }
            done();
          })
          .catch(done);
        pending += 1;
        this.$api
          .request(this.$url.Consoles.getSalesProduct, this.$method.get, { ...rankParams })
          .then(res => {
            if (res.code === 0) {
              this.ovLeaderProduct = Array.isArray(res.data) ? res.data : res.data.records || [];
            }
            done();
          })
          .catch(done);
      },
      money(v) {
        if (v === null || v === undefined || v === "") {
          return "$0.00";
        }
        const n = Number(v);
        return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
      },
      // 退款速览：汇总近期退款记录（endpoint 无日期过滤，展示为「近期」）
      loadRefundStats() {
        this.refundStatsLoading = true;
        this.$api
          .request(this.$url.RefundRecord.page, this.$method.get, { pageIndex: 1, pageSize: 200 })
          .then(res => {
            this.refundStatsLoading = false;
            const rows = res.code === 0 && res.data ? res.data.records || [] : [];
            const stats = { count: rows.length, amount: 0, success: 0, failed: 0 };
            rows.forEach(r => {
              stats.amount += Number(r.price) || 0;
              if (Number(r.rs) === 1) {
                stats.success += 1;
              } else if (Number(r.rs) === -1) {
                stats.failed += 1;
              }
            });
            stats.amount = Math.round(stats.amount * 100) / 100;
            this.refundStats = stats;
          })
          .catch(() => {
            this.refundStatsLoading = false;
          });
      },
      // ---------------- 按日期 ----------------
      loadDateTrend() {
        const params = { searchType: this.metric, type: this.dayTypeInt };
        if (this.dateRange && this.dateRange.length > 0) {
          params.startDate = this.dateRange[0];
          params.endDate = this.dateRange[1];
        }
        if (this.machineSel.length > 0) {
          params.equipmentNos = this.machineSel.join(",");
        }
        if (this.userName) {
          params.userName = this.userName;
        }
        this.$api.request(this.$url.DateProfit.day, this.$method.get, params).then(res => {
          const result = res.data || [];
          const xData = result.map(r => r.orderDate);
          const series = [{ name: this.metricLabel, data: result.map(r => r[this.metric]) }];
          this.renderChart("date-trend", trendLineOption({ xData, series, area: true }));
        });
      },
      dateSizeChange(pageSize) {
        this.datePage.pageSize = pageSize;
        this.loadDateTable();
      },
      loadDateTable(pageIndex) {
        this.dateLoading = true;
        this.dateError = false;
        this.datePage.pageIndex = pageIndex ? pageIndex : 1;
        const obj = { pageIndex: this.datePage.pageIndex, pageSize: this.datePage.pageSize };
        if (this.dateRange && this.dateRange.length > 0) {
          obj.startDate = this.dateRange[0];
          obj.endDate = this.dateRange[1];
        }
        if (this.granularity === "hour" && obj.startDate) {
          // 按小时：去掉时间部分
          obj.startDate = obj.startDate.slice(0, obj.startDate.indexOf(" "));
          obj.endDate = obj.endDate.slice(0, obj.endDate.indexOf(" "));
        } else if (this.granularity === "month" && obj.startDate) {
          obj.startDate = obj.startDate + "-01";
          obj.endDate = obj.endDate + "-" + this.getDaysInMonth(+obj.endDate.slice(0, 4), +obj.endDate.slice(5));
        }
        this.$api
          .request(this.$url.DateProfit.date, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.dateTableList = res.data.records || [];
              this.dateOrder = res.data.sum || { num: 0, sales: 0 };
              this.datePage.totalSize = parseInt(res.data.total) || 0;
            }
            this.dateLoading = false;
          })
          .catch(() => {
            this.dateLoading = false;
            this.dateError = true;
          });
      },
      getDaysInMonth(year, month) {
        return new Date(year, month, 0).getDate();
      },
      // ---------------- 按机器 ----------------
      loadMachineCharts() {
        const params = { searchType: this.metric, type: this.dayTypeInt };
        if (this.dateRange && this.dateRange.length > 0) {
          if (this.granularity === "day") {
            params.startDate = formatDate(this.dateRange[0], "yyyy-MM-dd");
            params.endDate = formatDate(this.dateRange[1], "yyyy-MM-dd");
          } else {
            params.startDate = this.dateRange[0];
            params.endDate = this.dateRange[1];
          }
        }
        if (this.machineSel.length > 0) {
          params.equipmentNos = this.machineSel.join(",");
        }
        if (this.productSel.length > 0) {
          params.productIds = this.productSel.join(",");
        }
        if (this.userName) {
          params.userName = this.userName;
        }
        this.$api.request(this.$url.MachineProfit.statisticsday, this.$method.get, params).then(res => {
          if (res.code !== 0 || !res.data) {
            return;
          }
          if (res.data.sales) {
            const pieData = res.data.sales.map(item => ({
              name: item.equipmentName,
              value: parseFloat(item[this.metric]) < 0 ? 0 : parseFloat(item[this.metric])
            }));
            this.renderChart("m-pie", pieOption({ data: pieData, name: this.$t("analytics.machineShare") }));
          }
          if (res.data.day) {
            this.renderTrendFromDay("m-trend", res.data.day, "equipmentNo", "equipmentName");
          }
        });
      },
      loadMachineRank() {
        const params = {
          type: this.dayTypeInt,
          searchType: this.metric,
          order: "desc",
          equipmentTop: parseInt(this.topN),
          goodsTop: 10
        };
        if (this.dateRange && this.dateRange.length > 0) {
          params.startDate = this.dateRange[0];
          params.endDate = this.dateRange[1];
        }
        if (this.userName) {
          params.userName = this.userName;
        }
        this.$api.request(this.$url.MachineProfit.statisticstop, this.$method.get, params).then(res => {
          if (res.code === 0 && res.data && res.data.length > 0) {
            this.machineRankData = res.data;
            this.machineSelectedNo = res.data[0].equipmentNo;
            this.machineRankNames = res.data.map(m => ({ equipmentNo: m.equipmentNo, equipmentName: m.equipmentName }));
            const categories = res.data.map(m => m.equipmentName);
            const values = res.data.map(m => (parseFloat(m[this.metric]) < 0 ? 0 : parseFloat(m[this.metric])));
            this.renderChart("m-rank", rankingBarOption({ categories, values, name: this.metricLabel }));
            this.renderMachineDrill();
          } else {
            this.machineRankData = [];
            this.machineRankNames = [];
          }
        });
      },
      renderMachineDrill() {
        const target = this.machineRankData.find(m => m.equipmentNo === this.machineSelectedNo);
        const stat = (target && target.productStat) || [];
        const categories = stat.map(s => s.goodsName);
        const values = stat.map(s => (parseFloat(s[this.metric]) < 0 ? 0 : parseFloat(s[this.metric])));
        this.renderChart("m-drill", rankingBarOption({ categories, values, name: this.metricLabel, horizontal: true }));
      },
      machineSizeChange(pageSize) {
        this.machinePage.pageSize = pageSize;
        this.loadMachineTable();
      },
      loadMachineTable(pageIndex) {
        this.machineLoading = true;
        this.machineError = false;
        this.machinePage.pageIndex = pageIndex ? pageIndex : 1;
        const lsearch = {};
        if (this.machineSel.length > 0) {
          lsearch.equipmentNos = this.machineSel.join(",");
        }
        if (this.productSel.length > 0) {
          lsearch.productIds = this.productSel.join(",");
        }
        const obj = this.$cloud.objMerge(lsearch, this.machinePage);
        obj.type = this.dayTypeInt;
        obj.searchType = this.metric;
        if (this.dateRange && this.dateRange.length > 0) {
          obj.startDate = this.dateRange[0];
          obj.endDate = this.dateRange[1];
        }
        this.$api
          .request(this.$url.MachineProfit.statisticstable, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.machineTableList = res.data.records || [];
              this.machinePage.totalSize = parseInt(res.data.total) || 0;
              this.machineOrder = res.data.sum || { num: 0, sales: 0 };
            }
            this.machineLoading = false;
          })
          .catch(() => {
            this.machineLoading = false;
            this.machineError = true;
          });
      },
      // ---------------- 按商品 ----------------
      loadProductCharts() {
        const params = { searchType: this.metric, type: this.dayTypeInt };
        if (this.dateRange && this.dateRange.length > 0) {
          if (this.granularity === "day") {
            params.startDate = formatDate(this.dateRange[0], "yyyy-MM-dd");
            params.endDate = formatDate(this.dateRange[1], "yyyy-MM-dd");
          } else {
            params.startDate = this.dateRange[0];
            params.endDate = this.dateRange[1];
          }
        }
        if (this.machineSel.length > 0) {
          params.equipmentNos = this.machineSel.join(",");
        }
        if (this.productSel.length > 0) {
          params.goodsIds = this.productSel.join(",");
        }
        if (this.userName) {
          params.userName = this.userName;
        }
        this.$api.request(this.$url.ProductProfit.statisticsday, this.$method.get, params).then(res => {
          if (res.code !== 0 || !res.data) {
            return;
          }
          if (res.data.sales) {
            const pieData = res.data.sales.map(item => ({
              name: item.goodsName,
              value: parseFloat(item[this.metric]) < 0 ? 0 : parseFloat(item[this.metric])
            }));
            this.renderChart("p-pie", pieOption({ data: pieData, name: this.$t("analytics.productShare") }));
          }
          if (res.data.day) {
            this.renderTrendFromDay("p-trend", res.data.day, "goodsId", "goodsName");
          }
        });
      },
      loadProductRank() {
        const params = {
          type: this.dayTypeInt,
          searchType: this.metric,
          order: "desc",
          equipmentTop: 10,
          goodsTop: parseInt(this.topN)
        };
        if (this.dateRange && this.dateRange.length > 0) {
          params.startDate = this.dateRange[0];
          params.endDate = this.dateRange[1];
        }
        if (this.userName) {
          params.userName = this.userName;
        }
        this.$api.request(this.$url.ProductProfit.statisticstop, this.$method.get, params).then(res => {
          if (res.code === 0 && res.data && res.data.length > 0) {
            this.productRankData = res.data;
            this.productSelectedId = res.data[0].goodsId;
            this.productRankNames = res.data.map(p => ({ goodsId: p.goodsId, goodsName: p.goodsName }));
            const categories = res.data.map(p => p.goodsName);
            const values = res.data.map(p => (parseFloat(p[this.metric]) < 0 ? 0 : parseFloat(p[this.metric])));
            this.renderChart("p-rank", rankingBarOption({ categories, values, name: this.metricLabel }));
            this.renderProductDrill();
          } else {
            this.productRankData = [];
            this.productRankNames = [];
          }
        });
      },
      renderProductDrill() {
        const target = this.productRankData.find(p => p.goodsId === this.productSelectedId);
        const stat = (target && target.equipmentStat) || [];
        const categories = stat.map(s => s.equipmentName);
        const values = stat.map(s => (parseFloat(s[this.metric]) < 0 ? 0 : parseFloat(s[this.metric])));
        this.renderChart("p-drill", rankingBarOption({ categories, values, name: this.metricLabel, horizontal: true }));
      },
      productSizeChange(pageSize) {
        this.productPage.pageSize = pageSize;
        this.loadProductTable();
      },
      loadProductTable(pageIndex) {
        this.productLoading = true;
        this.productError = false;
        this.productPage.pageIndex = pageIndex ? pageIndex : 1;
        const lsearch = {};
        if (this.machineSel.length > 0) {
          lsearch.equipmentNos = this.machineSel.join(",");
        }
        if (this.productSel.length > 0) {
          lsearch.goodsIds = this.productSel.join(",");
        }
        const obj = this.$cloud.objMerge(lsearch, this.productPage);
        obj.type = this.dayTypeInt;
        obj.searchType = this.metric;
        if (this.dateRange && this.dateRange.length > 0) {
          obj.startDate = this.dateRange[0];
          obj.endDate = this.dateRange[1];
        }
        this.$api
          .request(this.$url.ProductProfit.statisticstable, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              const records = res.data.records || [];
              // 序号（沿用原页面的分页序号规则）
              for (let i = 0; i < records.length; i++) {
                let step = 0;
                if (this.productPage.pageIndex > 1) {
                  step = 10;
                }
                records[i].xuhao = i + 1 + this.productPage.pageIndex * step;
              }
              this.productTableList = records;
              this.productPage.totalSize = parseInt(res.data.total) || 0;
              this.productOrder = res.data.sum || { num: 0, sales: 0 };
            }
            this.productLoading = false;
          })
          .catch(() => {
            this.productLoading = false;
            this.productError = true;
          });
      },
      // ---------------- 按用户 ----------------
      userSizeChange(pageSize) {
        this.userPage.pageSize = pageSize;
        this.loadUserTable();
      },
      loadUserTable(pageIndex) {
        this.userLoading = true;
        this.userError = false;
        this.userPage.pageIndex = pageIndex ? pageIndex : 1;
        if (this.dateRange && this.dateRange.length > 0) {
          this.userSearch.startDate = this.dateRange[0];
          this.userSearch.endDate = this.dateRange[1];
        }
        const obj = this.$cloud.objMerge(this.userSearch, this.userPage);
        this.$api
          .request(this.$url.Statistics.user, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.userTableList = res.data.records || [];
              this.userOrder = res.data.sum || { num: 0, sales: 0 };
              this.userPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.userLoading = false;
          })
          .catch(() => {
            this.userLoading = false;
            this.userError = true;
          });
      },
      // ---------------- 多序列走向图（由 day 对象构建） ----------------
      renderTrendFromDay(id, dayObj, idKey, nameKey) {
        const xData = Object.keys(dayObj);
        const buckets = Object.values(dayObj);
        const series = [];
        if (buckets.length > 0) {
          const first = buckets[0] || [];
          for (let i = 0; i < first.length; i++) {
            const entityId = first[i][idKey];
            const item = { name: first[i][nameKey] || "", data: [] };
            buckets.forEach(bucket => {
              let val = 0;
              for (let j = 0; j < bucket.length; j++) {
                if (bucket[j][idKey] === entityId) {
                  val = bucket[j][this.metric];
                }
              }
              item.data.push(val);
            });
            series.push(item);
          }
        }
        this.renderChart(id, trendLineOption({ xData, series }));
      },
      // ---------------- 汇总行（沿用原页面：num + parseFloat(sales)） ----------------
      summarize({ columns }, order) {
        const o = order || { num: 0, sales: 0 };
        const sums = [];
        columns.forEach((column, index) => {
          if (index === 0) {
            sums[index] = <span style="font-weight: bold;">{this.$t("machineRenewal.total")}</span>;
            return;
          }
          if (column.property === "num") {
            sums[index] = <span style="font-weight: bold;">{o.num}</span>;
          } else if (column.property === "sales") {
            sums[index] = <span style="font-weight: bold;">{parseFloat(o.sales)}</span>;
          }
        });
        return sums;
      },
      // ---------------- 懒加载下拉：机器 ----------------
      ensureMachineOptions() {
        if (!this.machineOptions.length) {
          this.getSelectMachine();
        }
      },
      loadMoreMachine() {
        this.getSelectMachine();
      },
      filterMachine(name) {
        this.machineOptions = [];
        this.selMachine.name = name;
        this.selMachine.pageNum = 1;
        this.getSelectMachine();
      },
      getSelectMachine() {
        if (this.lockMachine) {
          this.lockMachine = false;
          const timer = setTimeout(() => {
            clearTimeout(timer);
            this.lockMachine = true;
          }, 300);
        } else {
          return;
        }
        const params = {
          pageIndex: this.selMachine.pageNum,
          pageSize: this.selMachine.pageSize,
          searchName: this.selMachine.name,
          userName: this.userName
        };
        this.$api.request(this.$url.Machine.select, this.$method.get, params).then(res => {
          if (res.code === 0 && res.data) {
            if (res.data.records && res.data.records.length > 0) {
              this.selMachine.pageNum++;
            }
            if (res.data.current == 1) {
              this.machineOptions = res.data.records || [];
            } else if (res.data.records) {
              this.machineOptions = this.machineOptions.concat(res.data.records);
            }
            this.selMachine.pageSize = res.data.size;
          }
        });
      },
      // ---------------- 懒加载下拉：商品 ----------------
      ensureProductOptions() {
        if (!this.productOptions.length) {
          this.getSelectProduct();
        }
      },
      loadMoreProduct() {
        this.getSelectProduct();
      },
      filterProduct(name) {
        this.productOptions = [];
        this.selProduct.name = name;
        this.selProduct.pageNum = 1;
        this.getSelectProduct();
      },
      getSelectProduct() {
        if (this.lockProduct) {
          this.lockProduct = false;
          const timer = setTimeout(() => {
            clearTimeout(timer);
            this.lockProduct = true;
          }, 300);
        } else {
          return;
        }
        const params = {
          pageIndex: this.selProduct.pageNum,
          pageSize: this.selProduct.pageSize,
          searchName: this.selProduct.name,
          userName: this.userName
        };
        this.$api.request(this.$url.Product.select, this.$method.get, params).then(res => {
          if (res.code === 0 && res.data) {
            if (res.data.records && res.data.records.length > 0) {
              this.selProduct.pageNum++;
            }
            if (res.data.current == 1) {
              this.productOptions = res.data.records || [];
            } else if (res.data.records) {
              this.productOptions = this.productOptions.concat(res.data.records);
            }
            this.selProduct.pageSize = res.data.size;
          }
        });
      },
      // ---------------- 导出（沿用各原页面参数） ----------------
      handleExport() {
        if (this.mode === "overview") {
          this.$message.info(this.$t("analytics.overview"));
          return;
        }
        this.$confirm(this.$t("MachineProfit.export"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            if (this.mode === "user") {
              if (this.dateRange && this.dateRange.length > 0) {
                this.userSearch.startDate = this.dateRange[0];
                this.userSearch.endDate = this.dateRange[1];
              }
              this.$api.download(this.$url.Statistics.exportUser, this.userSearch);
              return;
            }
            if (!this.dateSpanOk()) {
              return;
            }
            const rData = { lang: this.$store.getters.lang };
            if (this.userName) {
              rData.userName = this.userName;
            }
            if (this.machineSel.length > 0) {
              rData.equipmentNos = this.machineSel;
            }
            if (this.dateRange && this.dateRange.length > 0) {
              rData.startDate = this.dateRange[0];
              rData.endDate = this.dateRange[1];
            }
            rData.searchType = this.metric;
            rData.type = this.dayTypeInt;
            if (this.mode === "date") {
              this.$api.download(this.$url.DateProfit.export, rData);
            } else if (this.mode === "machine") {
              if (this.productSel.length > 0) {
                rData.productIds = this.productSel;
              }
              this.$api.download(this.$url.MachineProfit.export, rData);
            } else if (this.mode === "product") {
              if (this.productSel.length > 0) {
                rData.goodsIds = this.productSel;
              }
              this.$api.download(this.$url.ProductProfit.export, rData);
            }
          })
          .catch(() => {});
      }
    }
  };
</script>

<style scoped lang="scss">
  .analytics-hub {
    padding-bottom: 24px;
  }

  .ah-seg-row {
    padding: 14px 16px 0;
  }

  .ah-seg ::v-deep .el-radio-button__inner {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  /* 控制条 */
  .ah-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 14px 16px;
    padding: 12px 16px;
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: 10px;
  }

  .ah-field {
    width: 200px;
  }

  .ah-field--sm {
    width: 150px;
  }

  .ah-field--date {
    width: 320px;
  }

  .ah-controls__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  /* 面板与卡片 */
  .ah-panel {
    padding: 0 16px;
  }

  .ah-card {
    background: var(--vtm-surface);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    padding: 14px 16px;
    margin-bottom: 16px;
  }

  .ah-card__title {
    font-family: var(--vtm-display);
    font-size: 14px;
    font-weight: 600;
    color: var(--vtm-heading);
    margin-bottom: 10px;
  }
  .ah-card__hint {
    font-family: var(--vtm-body);
    font-weight: 400;
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    margin-left: 6px;
  }
  .ah-refunds {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }
  .ah-refund-stat {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 14px 16px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface-2);
  }
  .ah-refund-stat__val {
    font-family: var(--vtm-display);
    font-size: 22px;
    font-weight: 700;
    color: var(--vtm-heading);
    line-height: 1.1;
  }
  .ah-refund-stat__label {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  @media (max-width: 720px) {
    .ah-refunds {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .ah-card__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
  }

  .ah-chart {
    width: 100%;
    height: 320px;
  }

  .ah-chart--tall {
    height: 380px;
  }

  .ah-grid {
    display: grid;
    gap: 16px;
  }

  .ah-grid--2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ah-grid--3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* 饼图窄、趋势图宽 */
  .ah-grid--pie-trend {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  }

  @media (max-width: 1100px) {
    .ah-grid--3,
    .ah-grid--2,
    .ah-grid--pie-trend {
      grid-template-columns: 1fr;
    }

    .ah-field,
    .ah-field--date {
      width: 100%;
    }

    .ah-controls__actions {
      margin-left: 0;
    }
  }
</style>
